import * as React from "react";
import {
  DisconnectButton,
  LeaveIcon,
  MediaDeviceMenu,
  TrackToggle,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { useTranslation } from "react-i18next";

type Props = {
  className?: string;
};

export function CustomControlBar({ className = "" }: Props) {
  const { t } = useTranslation();

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: false });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled]
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled]
  );

  return (
    <div className={`lk-control-bar relative w-full ${className}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center justify-center w-full">
        <div className="flex w-full gap-2 sm:w-auto md:justify-center">
          <div className="lk-button-group flex-1 sm:flex-none">
            <TrackToggle
              source={Track.Source.Microphone}
              className="lk-button w-full md:w-auto justify-center"
              onChange={microphoneOnChange}
              showIcon={true}
              title={t("controls.microphone")}
            >
              <span className="hidden md:inline">
                {t("controls.microphone")}
              </span>
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="audioinput"
                onActiveDeviceChange={(_k, id) =>
                  saveAudioInputDeviceId(id ?? "default")
                }
              />
            </div>
          </div>

          <div className="lk-button-group flex-1 sm:flex-none">
            <TrackToggle
              source={Track.Source.Camera}
              className="lk-button w-full md:w-auto justify-center"
              onChange={cameraOnChange}
              showIcon={true}
              title={t("controls.camera")}
            >
              <span className="hidden md:inline">{t("controls.camera")}</span>
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="videoinput"
                onActiveDeviceChange={(_k, id) =>
                  saveVideoInputDeviceId(id ?? "default")
                }
              />
            </div>
          </div>
        </div>

        <DisconnectButton
          className="lk-disconnect-button w-full sm:w-auto justify-center"
          title={t("controls.leave")}
        >
          <LeaveIcon className="mr-1" />
          {t("controls.leave")}
        </DisconnectButton>
      </div>
    </div>
  );
}
